import React, {useEffect, useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import {useTranslation} from 'react-i18next';
import {Collapse, UnmountClosed} from 'react-collapse';
import {connect} from 'react-redux';
import {Alert, Form, Input, Select} from 'antd';
import {StyledAddPaymentForm} from './StyledAddPaymentForm';
import VendorSelectorWithAuth from '../VendorSelectorWithAuth';
import EuroInput from '../../../EuroInput';
import InvoiceUploader from '../InvoiceUploader';
import VendorPaymentsList from '../VendorPaymentsList';
import AuthenticationWindow from '../../CardsPage/AuthenticationWindow';
import {formHelpers, scaHelpers, subscriptionsHelpers} from '../../../../utils/helpers';
import {validation} from '../../../../utils/validation';
import {expenseActions} from '../../../../state/actions/expense.actions';
import {subscriptionActions} from '../../../../state/actions/subscription.actions';
import {vendorsActions} from '../../../../state/actions/vendors.actions';
import {SCAActionsConstants} from '../../../../constants';

const {Item} = Form;

const initialFormValues = {
  description: '',
  invoice_id: undefined,
  price: undefined,
  subscription_id: undefined,
  vendor_local_id: undefined
}

const defaultData = {data: [], loading: false};

const SCA_OPERATION_NAME = SCAActionsConstants.PAYMENT_CREATE_ACTION;

const AddPaymentForm = React.forwardRef(({
  dispatch,
  createPayment,
  getExpenseAttachment,
  getVendorPayments,
  getVendorSubscriptions,
  walletId,

  additionalFormValues = {},
  initialFormData = {},
  beforeSubmit,
  clearAfterSubmit = true,
  onError,
  onValuesChange,
  onSuccess,
  size = 'full',
  showInvoiceButton = true,
  vendorSelectorProps = {},
  ...rest
}, ref) => {
  const [t] = useTranslation('main');
  const [form] = Form.useForm();
  const [authWindowProps, setAuthWindowProps] = useState({loading: false, open: false});
  const [attachments, setAttachments] = useState([]);
  const [error, setError] = useState(null);
  const [isDisabled, setIsDisabled] = useState(false);
  const [subscriptions, setSubscriptions] = useState(defaultData);
  const [payments, setPayments] = useState(defaultData);

  const vendorId = Form.useWatch('vendor_local_id', form);

  const validationRules = useMemo(() => ({
    required: validation.required(t),
  }), [t]);

  const requiredItemProps = useMemo(() => ({rules: validationRules.required, required: true}), [validationRules.required]);

  const productOptions = useMemo(() => subscriptionsHelpers.getSubscriptionOptions(subscriptions?.data || []), [subscriptions?.data]); // eslint-disable-line react-hooks/exhaustive-deps

  const hasMultipleProducts = useMemo(() => productOptions.length > 1, [productOptions]);

  useEffect(() => {
    if (productOptions.length === 1) {
      form.setFieldValue('subscription_id', productOptions[0].value);
    }
  }, [form, productOptions]);

  useEffect(() => {
    if (error) setError(null);
  }, [vendorId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadSubscriptions = (id) => {
    if (!id) return;

    setSubscriptions({...subscriptions, loading: true});
    getVendorSubscriptions({
      id,
      successCallback: ({subscriptions: data}) => setSubscriptions({data, loading: false}),
      errorCallback: () => setSubscriptions(defaultData)
    });
  }

  const loadPayments = (id) => {
    if (!id) return;

    setPayments({...payments, loading: true});
    getVendorPayments({
      id,
      query: { sort_order: 'desc' },
      successCallback: (data) => setPayments({data, loading: false}),
      errorCallback: () => setPayments(defaultData)
    });
  }

  useEffect(() => {
    form.setFieldsValue(initialFormData);
  }, [initialFormData, form]);

  useEffect(() => {
    form.setFieldValue('product_id', undefined);

    if (vendorId) {
      loadSubscriptions(vendorId);
      loadPayments(vendorId);
    } else {
      setAttachments([]);
      setPayments(defaultData);
      setSubscriptions(defaultData);
    }
  }, [vendorId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLoadAttachment = (expenseId, onComplete) => {
    if (!expenseId || attachments.some(att => att.id === expenseId)) {
      onComplete?.();
      return;
    }

    getExpenseAttachment({
      id: expenseId,
      successCallback: (data) => {
        const contentType = data['content-type'];
        const attachment = {
          contentType,
          data,
          id: expenseId,
          file: `data:${contentType};base64,${data.file}`,
          name: data.filename
        };
        setAttachments([...attachments, attachment]);
        onComplete?.();
      },
      errorCallback: () => {
        onComplete?.();
      }
    });
  }

  const handleSubmit = (formData) => {
    const data = {
      ...formData,
      ...additionalFormValues,
      wallet_id: walletId
    };

    beforeSubmit && beforeSubmit();

    setIsDisabled(true);

    createPayment({
      data,
      headers: scaHelpers.getAuthHeaders(SCA_OPERATION_NAME),
      successCallback: (response) => {
        setIsDisabled(false);
        onSuccess && onSuccess(response);

        if (clearAfterSubmit) {
          form.resetFields();
          form.setFieldsValue(initialFormData);
        }
      },
      errorCallback: (response) => {
        scaHelpers.SCAResponseCallback({
          response,
          scaCallback: (scaProps) => setAuthWindowProps({...authWindowProps, ...scaProps}),
          errorCallback: (err) => {
            const fields = formHelpers.getFormServerErrorFields(err.response.data);
            form.setFields(fields);
            setIsDisabled(false);
          }
        });
      }
    });
  }

  const handleCloseAuthModal = () => {
    onError?.();
    setIsDisabled(false);
    setAuthWindowProps({...authWindowProps, open: false});
  }

  const handleSuccessAuth = () => {
    setAuthWindowProps({...authWindowProps, open: false});
    form.submit();
  }

  const handleUploadInvoice = (invoice) => form.setFieldValue('invoice_id', invoice?.id);

  const placeholderEnter = (key) => t('placeholderTemplates.enter', {name: t(key).toLowerCase()});

  const placeholderSelect = (key) => t('placeholderTemplates.select', {name: t(key).toLowerCase()});

  return (
    <StyledAddPaymentForm
      size={size}
      {...rest}
    >
      <UnmountClosed isOpened={payments.data.length}>
        <VendorPaymentsList
          attachments={attachments}
          className='vendor-payments-list'
          onAttachmentLoad={handleLoadAttachment}
          payments={payments.data}
        />
      </UnmountClosed>

      <Form
        className={isDisabled ? 'disabled-form' : ''}
        disabled={isDisabled}
        initialValues={{...initialFormValues, ...additionalFormValues}}
        form={form}
        layout='vertical'
        onFinish={handleSubmit}
        onValuesChange={onValuesChange}
        ref={ref}
      >
        <Item
          {...requiredItemProps}
          label={t('vendor')}
          name='vendor_local_id'
        >
          <VendorSelectorWithAuth
            {...vendorSelectorProps}
            vendorFormSize='short'
            selectProps={{
              className: 'vendor-selector',
              placeholder: placeholderSelect('vendor')
            }}
          />
        </Item>

        <UnmountClosed isOpened={hasMultipleProducts}>
          <Item
            {...requiredItemProps}
            label={t('product')}
            name='product_id'
          >
            <Select
              loading={subscriptions.loading}
              disabled={isDisabled ? true : !vendorId}
              optionFilterProp='optionLabel'
              options={productOptions}
              placeholder={placeholderSelect('product')}
              size='large'
              showSearch
            />
          </Item>
        </UnmountClosed>

        <Item
          {...requiredItemProps}
          label={t('amount')}
          name='price'
        >
          <EuroInput placeholder={placeholderEnter('amount')} />
        </Item>
        <Item
          className={hasMultipleProducts && 'w-100'}
          label={t('reason')}
          name='description'
        >
          <Input
            placeholder={placeholderSelect('reason')}
            size='large'
          />
        </Item>

        {showInvoiceButton && (
          <Item name='invoice_id'>
            <InvoiceUploader onChange={handleUploadInvoice} />
          </Item>
        )}

        <Collapse isOpened={error}>
          <Alert message={error} type='error' showIcon />
        </Collapse>
      </Form>

      <AuthenticationWindow
        {...authWindowProps}
        authModalProps={{title: `${t('new')} ${t('payment')}`}}
        handleCancel={handleCloseAuthModal}
        onSuccess={handleSuccessAuth}
        operationName={SCA_OPERATION_NAME}
      />
    </StyledAddPaymentForm>
  );
});

AddPaymentForm.propTypes = {
  additionalFormValues: PropTypes.object,
  beforeSubmit: PropTypes.func,
  clearAfterSubmit: PropTypes.bool,
  initialFormData: PropTypes.object,
  onValuesChange: PropTypes.func,
  onSuccess: PropTypes.func,
  size: PropTypes.oneOf(['full', 'half']),
  showInvoiceButton: PropTypes.bool,
  vendorSelectorProps: PropTypes.object
}

const mapStateToProps = ({banking}) => ({
  walletId: banking?.companyDetails?.wallet_id,
});

const mapDispatchToProps = {
  createPayment: vendorsActions.createPayment,
  getExpenseAttachment: expenseActions.getExpenseAttachment,
  getVendorPayments: vendorsActions.getVendorPayments,
  getVendorSubscriptions: subscriptionActions.getVendorSubscriptions,
}

export default connect(mapStateToProps, mapDispatchToProps, null, {forwardRef: true})(AddPaymentForm);
